import { useNavigate } from '@tanstack/react-router'
import { Users, Activity, TrendingUp, AlertTriangle, Clock, RefreshCw, CheckCircle, XCircle } from 'lucide-react'
import { useDashboardStats, useDashboardActivity } from '../hooks/useDashboard'

const EMPTY_STATS = {
  totalCustomers: 0,
  activeSubscriptions: 0,
  totalApiCalls: 0,
  recentFailures: 0,
  overdueScheduled: 0,
  expiringTrialWorkspaces: 0,
}

export default function Dashboard() {
  const navigate = useNavigate()
  const {
    data: stats = EMPTY_STATS,
    isLoading: statsLoading,
    isRefetching: statsRefreshing,
    refetch: refetchStats,
    error: statsError,
    dataUpdatedAt,
  } = useDashboardStats()
  const {
    data: recentActivity = [],
    isLoading: activityLoading,
    isRefetching: activityRefreshing,
    refetch: refetchActivity,
  } = useDashboardActivity()

  const loading = statsLoading || activityLoading
  const refreshing = statsRefreshing || activityRefreshing
  const lastUpdated = dataUpdatedAt ? new Date(dataUpdatedAt) : null

  const refresh = () => {
    refetchStats()
    refetchActivity()
  }

  if (loading) {
    return <div className="loading">Loading dashboard...</div>
  }

  const successCount = recentActivity.filter(a => a.type === 'success').length
  const failedCount = recentActivity.filter(a => a.type === 'failed').length

  return (
    <div className="dashboard">
      <div className="page-header">
        <h1>Dashboard</h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          {lastUpdated && (
            <span style={{ fontSize: '0.8rem', color: 'var(--gray-500)' }}>
              Updated {lastUpdated.toLocaleTimeString()}
            </span>
          )}
          <button
            className="btn-secondary"
            onClick={refresh}
            disabled={refreshing}
            style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
          >
            <RefreshCw size={14} style={{ animation: refreshing ? 'spin 0.8s linear infinite' : 'none' }} />
            Refresh
          </button>
        </div>
      </div>

      {statsError && (
        <div style={{ marginBottom: '1rem', padding: '0.75rem 1rem', borderLeft: '3px solid var(--danger)', background: '#fef2f2', color: 'var(--danger)', fontSize: '0.875rem', borderRadius: '4px' }}>
          Failed to load stats — {statsError.message}
        </div>
      )}

      {/* Stat Cards */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon blue">
            <Users size={24} />
          </div>
          <div className="stat-content">
            <p className="stat-label">Total Customers</p>
            <p className="stat-value">{stats.totalCustomers}</p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon green">
            <Activity size={24} />
          </div>
          <div className="stat-content">
            <p className="stat-label">Active Subscriptions</p>
            <p className="stat-value">{stats.activeSubscriptions}</p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon purple">
            <TrendingUp size={24} />
          </div>
          <div className="stat-content">
            <p className="stat-label">API Calls (30d)</p>
            <p className="stat-value">{stats.totalApiCalls.toLocaleString()}</p>
          </div>
        </div>

        <div className="stat-card" style={{ borderLeft: stats.recentFailures > 0 ? '3px solid var(--danger)' : undefined }}>
          <div className="stat-icon red">
            <AlertTriangle size={24} />
          </div>
          <div className="stat-content">
            <p className="stat-label">Recent Failures (7d)</p>
            <p className="stat-value" style={{ color: stats.recentFailures > 0 ? 'var(--danger)' : undefined }}>
              {stats.recentFailures}
            </p>
          </div>
        </div>

        <div
          className="stat-card clickable"
          onClick={() => navigate({ to: '/inventory', search: { filter: 'overdue' } })}
          title="Click to view overdue scheduled items"
          style={{ borderLeft: stats.overdueScheduled > 0 ? '3px solid var(--warning)' : undefined }}
        >
          <div className="stat-icon orange">
            <Clock size={24} />
          </div>
          <div className="stat-content">
            <p className="stat-label">Overdue Scheduled</p>
            <p className="stat-value" style={{ color: stats.overdueScheduled > 0 ? 'var(--warning)' : undefined }}>
              {stats.overdueScheduled}
            </p>
            <p className="stat-hint">No runs in 2 days • Click to view</p>
          </div>
        </div>

        <div
          className="stat-card clickable"
          onClick={() => navigate({ to: '/inventory', search: { filter: 'trial' } })}
          title="Click to view trial workspaces"
          style={{ borderLeft: stats.expiringTrialWorkspaces > 0 ? '3px solid var(--warning)' : undefined }}
        >
          <div className="stat-icon orange">
            <Clock size={24} />
          </div>
          <div className="stat-content">
            <p className="stat-label">Expiring Trials</p>
            <p className="stat-value" style={{ color: stats.expiringTrialWorkspaces > 0 ? 'var(--warning)' : undefined }}>
              {stats.expiringTrialWorkspaces}
            </p>
            <p className="stat-hint">Workspaces expiring within 14d • Click to view</p>
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="recent-activity">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
          <h2 style={{ margin: 0, fontSize: '18px' }}>Recent Activity</h2>
          <div style={{ display: 'flex', gap: '0.75rem', fontSize: '0.8rem', color: 'var(--gray-600)' }}>
            {recentActivity.length > 0 && (
              <>
                <span style={{ display: 'flex', alignItems: 'center', gap: '4px', color: 'var(--success)' }}>
                  <CheckCircle size={14} /> {successCount} success
                </span>
                {failedCount > 0 && (
                  <span style={{ display: 'flex', alignItems: 'center', gap: '4px', color: 'var(--danger)' }}>
                    <XCircle size={14} /> {failedCount} failed
                  </span>
                )}
              </>
            )}
          </div>
        </div>

        <div className="activity-list">
          {recentActivity.length === 0 ? (
            <p className="empty-state">No recent activity</p>
          ) : (
            recentActivity.map((activity, index) => (
              <div key={index} className="activity-item">
                <div className={`activity-badge ${activity.type}`}>
                  {activity.type === 'success' ? '✓' : activity.type === 'running' ? '◷' : '✗'}
                </div>
                <div className="activity-details">
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <p className="activity-customer" style={{ margin: 0 }}>{activity.customer_name}</p>
                    {activity.item_type && (
                      <span style={{
                        fontSize: '0.65rem',
                        padding: '1px 6px',
                        borderRadius: '8px',
                        background: activity.item_type === 'datapipeline' ? '#f3e5f5' : '#e3f2fd',
                        color: activity.item_type === 'datapipeline' ? '#7b1fa2' : '#1976d2',
                        fontWeight: 600,
                        textTransform: 'uppercase'
                      }}>
                        {activity.item_type === 'datapipeline' ? 'PIPELINE' : 'NOTEBOOK'}
                      </span>
                    )}
                  </div>
                  <p className="activity-message">
                    {activity.item_name || '—'}
                    {activity.workspace_name && (
                      <span style={{ color: 'var(--gray-500)' }}> · {activity.workspace_name}</span>
                    )}
                    <span style={{
                      marginLeft: '0.5rem',
                      color: activity.type === 'success' ? 'var(--success)' : activity.type === 'running' ? 'var(--warning)' : 'var(--danger)',
                      fontWeight: 500
                    }}>
                      {activity.status}
                    </span>
                  </p>
                </div>
                <div className="activity-time">
                  {activity.timestamp ? new Date(activity.timestamp).toLocaleString() : '—'}
                </div>
              </div>
            ))
          )}
        </div>

        {recentActivity.length > 0 && (
          <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid var(--border)', display: 'flex', gap: '1rem' }}>
            <button className="btn-secondary" style={{ fontSize: '0.8rem' }} onClick={() => navigate({ to: '/analytics' })}>
              View Full Analytics →
            </button>
            <button className="btn-secondary" style={{ fontSize: '0.8rem' }} onClick={() => navigate({ to: '/inventory' })}>
              View Inventory →
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
